import { useCallback, useMemo, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { SelectButton } from "primereact/selectbutton";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import "primereact/resources/themes/lara-light-cyan/theme.css";
import "primereact/resources/primereact.min.css";
import * as api from "../../utils/Api.ts";
import type { ReportesFiltros } from "../../utils/Api.ts";
import { showMsg } from "../../utils/Constants.tsx";
import ChartCard from "../../components/widgets/graphs/ChartCard.tsx";
import KpiCard from "../../components/widgets/graphs/KpiCard.tsx";
import MultiBarChart from "../../components/widgets/graphs/MultiBarChart.tsx";
import {
  CHART_COLORS,
  formatMoney,
  formatMoneyCompact,
  formatNumber,
  formatPercent,
} from "../../components/widgets/graphs/chartTheme.ts";
import {
  Count,
  FilterLink,
  Money,
  PctBar,
} from "../../components/reportes/ReportesCells.tsx";
import {
  clean,
  estadoAprobacionColor,
  exportSheets,
  formatEtapa,
  normalizarEstadosOrden,
  useCatalogos,
  useReporte,
  useScrollbarGutter,
} from "../../components/reportes/reportesUtils.ts";
import "../finanzas/dashboard.css";
import "../../components/reportes/reportes.css";

/* ------------------------------------------------------------------ */
/* Types (shape returned by /reports/*)                                 */
/* ------------------------------------------------------------------ */

interface PresupuestoTotales {
  anio: number;
  presupuestoAnualClp: number;
  presupuestoALaFechaClp: number;
  ejecutadoClp: number;
  enAprobacionClp: number;
  solicitudesSinOrdenClp: number;
  facturadoClp: number;
  disponibleClp: number;
  disponibleProyectadoClp: number;
  porcentajeEjecucion: number;
  porcentajeEjecucionALaFecha: number;
  estado: string;
}

interface MesRow {
  mes: number;
  nombre: string;
  presupuestoClp: number;
  ejecutadoClp: number;
  enAprobacionClp: number;
  solicitudesSinOrdenClp: number;
  facturadoClp: number;
  desviacionClp: number;
  porcentajeEjecucion: number;
  presupuestoAcumuladoClp: number;
  ejecutadoAcumuladoClp: number;
  facturadoAcumuladoClp: number;
  porcentajeEjecucionAcumulado: number;
}

interface Mensual {
  anio: number;
  totales: PresupuestoTotales;
  meses: MesRow[];
}

interface CentroCostoPpto {
  idCentroCosto: number;
  codigo: string;
  nombre: string;
  area: string;
  presupuestoClp: number;
  presupuestoALaFechaClp: number;
  ejecutadoClp: number;
  enAprobacionClp: number;
  solicitudesPendientesClp: number;
  solicitudesAprobadasSinOrdenClp: number;
  facturadoClp: number;
  disponibleClp: number;
  porcentajeEjecucion: number;
  estado: string;
}

interface CentrosCostoPpto {
  anio: number;
  mesesTranscurridos: number;
  data: CentroCostoPpto[];
}

interface CuentaRow {
  codigoNaturaleza: number | null;
  naturaleza: string | null;
  codigoCuenta: number | null;
  cuenta: string | null;
  codigoCuentaItem: number | null;
  cuentaItem: string | null;
  cantidadCentrosCosto: number;
  presupuestoClp: number;
  ejecutadoClp: number;
  enAprobacionClp: number;
  solicitudesSinOrdenClp: number;
  facturadoClp: number;
  disponibleClp: number;
  porcentajeEjecucion: number;
  estado: string;
}

interface Cuentas {
  anio: number;
  data: CuentaRow[];
}

interface EstadoRow {
  idEstado: number;
  codigo: string;
  nombre: string;
  cantidad: number;
  montoClp: number;
}

interface SolicitudesEstados {
  porSolicitud: EstadoRow[];
  porLinea: EstadoRow[];
}

interface SolicitudCentroCosto {
  idCentroCosto: number;
  montoSolicitadoClp: number;
  montoAprobadoClp: number;
}

interface SolicitudPeriodo {
  periodo: string;
  montoSolicitadoClp: number;
  montoAprobadoClp: number;
}

interface NaturalezasItems {
  totales: {
    asociaciones: number;
    naturalezasConItems: number;
    itemsAsociados: number;
    itemsSinAsociar: number;
    itemsMultiples: number;
    montoItemsSinAsociarClp: number;
  };
  naturalezas: (CuentaRow & { itemsAsociados: unknown[] })[];
  itemsPorRevisar: Record<string, unknown>[];
}

interface Filtros {
  anio: number;
  version: number;
  idArea: number | null;
  centrosCosto: number[];
}

// Row of the grouped tables (área / naturaleza / cuenta / item)
interface GrupoRow {
  key: string;
  nombre: string;
  sub?: string;
  presupuestoClp: number;
  solicitadoClp: number | null;
  gastadoClp: number;
  enAprobacionClp: number;
  solicitudesSinOrdenClp: number;
  disponibleClp: number;
  porcentajeEjecucion: number;
  idArea?: number;
}

/* ------------------------------------------------------------------ */
/* Constants                                                            */
/* ------------------------------------------------------------------ */

// "Gastado" = purchase orders whose approval state is >= 6 (Aprobada,
// Confirmada parcial, Confirmada, Pendiente GG, Confirmada GG). The backend
// ejecutadoClp follows the same rule; the KPI recomputes it from the states
const ESTADO_GASTADO_MIN = 6;

const VERSION_OPTIONS = [
  { label: "Base", value: 0 },
  { label: "Reformulado", value: 1 },
];

const VISTA_MENSUAL_OPTIONS = [
  { label: "Mensual", value: "mensual" },
  { label: "Acumulado", value: "acumulado" },
];

const AGRUPAR_CUENTAS_OPTIONS = [
  { label: "Naturaleza", value: "naturaleza" },
  { label: "Cuenta contable", value: "cuenta" },
  { label: "Item contable", value: "item" },
];

const COLOR_PRESUPUESTO = CHART_COLORS.contabilizado;
const COLOR_SOLICITADO = CHART_COLORS.pendiente;
const COLOR_GASTADO = CHART_COLORS.facturado;

const TOP_CHART = 10;
const ANIOS_COMPARACION = 3;
const MESES_CORTOS = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

const ESTADO_PPTO_SEVERITY: Record<
  string,
  "success" | "danger" | "warning" | "secondary"
> = {
  NORMAL: "success",
  SOBREEJECUTADO: "danger",
  SIN_PRESUPUESTO: "secondary",
};

const defaultFiltros = (): Filtros => ({
  anio: new Date().getFullYear(),
  version: 0,
  idArea: null,
  centrosCosto: [],
});

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

const sum = <T,>(rows: T[], pick: (r: T) => number) =>
  rows.reduce((a, r) => a + (Number(pick(r)) || 0), 0);

const pct = (part: number, total: number) => (total ? (part / total) * 100 : 0);

// Budget endpoints take anio + version; the others take the calendar year as
// a date range. Only the params each DTO accepts are sent
const filtrosComunes = (f: Filtros): ReportesFiltros => ({
  centrosCosto: f.centrosCosto.length ? f.centrosCosto.join(",") : undefined,
  idArea: f.idArea ?? undefined,
});

const filtrosPresupuesto = (f: Filtros, anio = f.anio): ReportesFiltros => ({
  ...filtrosComunes(f),
  anio,
  version: f.version,
});

const filtrosAnio = (f: Filtros): ReportesFiltros => ({
  ...filtrosComunes(f),
  fechaDesde: `${f.anio}-01-01`,
  fechaHasta: `${f.anio}-12-31`,
});

const estadoPptoTag = (estado: string) => (
  <Tag
    value={formatEtapa(estado)}
    severity={ESTADO_PPTO_SEVERITY[estado] ?? "warning"}
  />
);

const ejecucionBody = (value: number, presupuesto: number) =>
  presupuesto ? (
    <PctBar
      value={value}
      color={value > 100 ? CHART_COLORS.diferencia : COLOR_GASTADO}
    />
  ) : (
    <span className="fd-sub">Sin presupuesto</span>
  );

// Amounts by approval state, colored like the rest of the app; states >= 6
// are marked because they count as "aprobado" (solicitudes) / "gastado" (OC)
const estadosChart = (rows: EstadoRow[]) => {
  const visibles = rows.filter((r) => r.montoClp !== 0 || r.cantidad > 0);
  return {
    labels: visibles.map(
      (r) => `${r.nombre}${r.idEstado >= ESTADO_GASTADO_MIN ? " (≥6)" : ""}`,
    ),
    series: [
      {
        label: "Monto",
        data: visibles.map((r) => r.montoClp),
        color: visibles.map((r) => estadoAprobacionColor(r.codigo)),
      },
    ],
  };
};

// Rejected / cancelled states: never pending approval
const ESTADOS_DESCARTADOS = [2, 3, 4];

/* ------------------------------------------------------------------ */
/* Screen                                                               */
/* ------------------------------------------------------------------ */

export default function IndexPresupuesto() {
  const toast = useRef<Toast>(null);
  useScrollbarGutter();

  const [form, setForm] = useState<Filtros>(defaultFiltros);
  const [applied, setApplied] = useState<Filtros>(defaultFiltros);
  const [vistaMensual, setVistaMensual] = useState("mensual");
  const [agruparCuentas, setAgruparCuentas] = useState("naturaleza");
  const [mostrarItems, setMostrarItems] = useState(false);
  const [exporting, setExporting] = useState(false);

  const key = JSON.stringify(applied);
  const isDirty = JSON.stringify(form) !== key;
  const catalogos = useCatalogos();
  const R = api.GET_REPORTES;

  const mensual = useReporte<Mensual>(
    () => R.getPresupuestoMensual(filtrosPresupuesto(applied)),
    key,
  );
  // Previous years, for the yearly comparison
  const aniosPrevios = useReporte<Mensual[]>(
    () =>
      Promise.all(
        Array.from({ length: ANIOS_COMPARACION - 1 }, (_, i) =>
          R.getPresupuestoMensual(
            filtrosPresupuesto(
              applied,
              applied.anio - (ANIOS_COMPARACION - 1) + i,
            ),
          ),
        ),
      ),
    key,
  );
  const centros = useReporte<CentrosCostoPpto>(
    () => R.getPresupuestoCentrosCosto(filtrosPresupuesto(applied)),
    key,
  );
  const cuentas = useReporte<Cuentas>(
    () => R.getPresupuestoCuentas(filtrosPresupuesto(applied)),
    key,
  );
  const solEstados = useReporte<SolicitudesEstados>(
    () => R.getSolicitudesEstados(filtrosAnio(applied)),
    key,
  );
  // Per OC rows (gastado = OCs in state >= 6), whatever the response shape
  const ordEstados = useReporte<EstadoRow[]>(
    () =>
      R.getOrdenesEstados(filtrosAnio(applied)).then(
        (raw: unknown) => normalizarEstadosOrden<EstadoRow>(raw).porOrden,
      ),
    key,
  );
  const solPeriodos = useReporte<SolicitudPeriodo[]>(
    () =>
      R.getSolicitudesPeriodos({ ...filtrosAnio(applied), agrupacion: "mes" }),
    key,
  );
  const solCentros = useReporte<SolicitudCentroCosto[]>(
    () =>
      R.getSolicitudesCentrosCosto({
        ...filtrosAnio(applied),
        limit: 5000,
        ordenarPor: "monto",
      }),
    key,
  );

  const reportes = [
    mensual,
    aniosPrevios,
    centros,
    cuentas,
    solEstados,
    ordEstados,
    solPeriodos,
    solCentros,
  ];
  const anyLoading = reportes.some((r) => r.loading);
  const reloadAll = () => reportes.forEach((r) => r.reload());

  /* ------------------------- filters ------------------------- */

  const areaOptions = useMemo(
    () =>
      (catalogos.data?.areas ?? []).map((a) => ({
        label: clean(a.nombre),
        value: a.id,
      })),
    [catalogos.data],
  );

  const centroCostoOptions = useMemo(
    () =>
      (catalogos.data?.centrosCosto ?? [])
        .filter((c) => !form.idArea || c.idArea === form.idArea)
        .map((c) => ({ label: clean(c.nombre), value: c.id }))
        .sort((a, b) => a.label.localeCompare(b.label, "es")),
    [catalogos.data, form.idArea],
  );

  const anioOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => ({
      label: String(y + 1 - i),
      value: y + 1 - i,
    }));
  }, []);

  const patchBoth = useCallback((patch: Partial<Filtros>) => {
    setForm((f) => ({ ...f, ...patch }));
    setApplied((f) => ({ ...f, ...patch }));
  }, []);

  const filtrarCentroCosto = useCallback(
    (idCentroCosto: number | undefined) => {
      if (idCentroCosto) patchBoth({ centrosCosto: [idCentroCosto] });
    },
    [patchBoth],
  );

  const filtrarArea = useCallback(
    (idArea: number | undefined) => {
      if (idArea) patchBoth({ idArea, centrosCosto: [] });
    },
    [patchBoth],
  );

  const onAreaChange = (idArea: number | null) => {
    const allowed = new Set(
      (catalogos.data?.centrosCosto ?? [])
        .filter((c) => !idArea || c.idArea === idArea)
        .map((c) => c.id),
    );
    setForm((f) => ({
      ...f,
      idArea,
      centrosCosto: f.centrosCosto.filter((id) => allowed.has(id)),
    }));
  };

  const activeCount =
    (applied.idArea ? 1 : 0) + (applied.centrosCosto.length ? 1 : 0);

  /* ------------------------- derived data ------------------------- */

  const t = mensual.data?.totales;
  const presupuesto = t?.presupuestoAnualClp ?? 0;
  const presupuestoMeses = sum(
    mensual.data?.meses ?? [],
    (m) => m.presupuestoClp,
  );

  const estadosOC = useMemo(() => ordEstados.data ?? [], [ordEstados.data]);
  const gastado = sum(
    estadosOC.filter((e) => e.idEstado >= ESTADO_GASTADO_MIN),
    (e) => e.montoClp,
  );
  const lineas = useMemo(
    () => solEstados.data?.porLinea ?? [],
    [solEstados.data],
  );
  const solicitado = sum(lineas, (e) => e.montoClp);
  const solicitadoAprobado = sum(
    lineas.filter((e) => e.idEstado >= ESTADO_GASTADO_MIN),
    (e) => e.montoClp,
  );
  const disponible = presupuesto - gastado;
  // OC still waiting for approval: state < 6, not rejected / cancelled
  const enAprobacion = sum(
    estadosOC.filter(
      (e) =>
        e.idEstado < ESTADO_GASTADO_MIN &&
        !ESTADOS_DESCARTADOS.includes(e.idEstado),
    ),
    (e) => e.montoClp,
  );

  // Solicitado by CC, joined into the budget rows
  const solicitadoPorCC = useMemo(
    () =>
      new Map(
        (solCentros.data ?? []).map((c) => [
          c.idCentroCosto,
          c.montoSolicitadoClp,
        ]),
      ),
    [solCentros.data],
  );

  const ccRows = useMemo(
    () =>
      (centros.data?.data ?? []).map((c) => ({
        ...c,
        solicitadoClp: solicitadoPorCC.get(c.idCentroCosto) ?? 0,
      })),
    [centros.data, solicitadoPorCC],
  );

  const topCentros = useMemo(
    () =>
      [...ccRows]
        .sort(
          (a, b) =>
            Math.max(b.ejecutadoClp, b.presupuestoClp) -
            Math.max(a.ejecutadoClp, a.presupuestoClp),
        )
        .slice(0, TOP_CHART),
    [ccRows],
  );

  const centrosChart = useMemo(
    () => ({
      labels: topCentros.map((c) => clean(c.nombre)),
      series: [
        {
          label: "Presupuesto",
          data: topCentros.map((c) => c.presupuestoClp),
          color: COLOR_PRESUPUESTO,
        },
        {
          label: "Solicitado",
          data: topCentros.map((c) => c.solicitadoClp),
          color: COLOR_SOLICITADO,
        },
        {
          label: "Gastado",
          data: topCentros.map((c) => c.ejecutadoClp),
          color: COLOR_GASTADO,
        },
      ],
    }),
    [topCentros],
  );

  const onCentroBarClick = useCallback(
    (index: number) => filtrarCentroCosto(topCentros[index]?.idCentroCosto),
    [topCentros, filtrarCentroCosto],
  );

  // Áreas: sum of their cost centers
  const areaRows = useMemo<GrupoRow[]>(() => {
    const areaId = new Map(
      (catalogos.data?.areas ?? []).map((a) => [clean(a.nombre), a.id]),
    );
    const byArea = new Map<string, GrupoRow>();
    ccRows.forEach((c) => {
      const nombre = clean(c.area) || "Sin área";
      const acc =
        byArea.get(nombre) ??
        ({
          key: nombre,
          nombre,
          presupuestoClp: 0,
          solicitadoClp: 0,
          gastadoClp: 0,
          enAprobacionClp: 0,
          solicitudesSinOrdenClp: 0,
          disponibleClp: 0,
          porcentajeEjecucion: 0,
          idArea: areaId.get(nombre),
        } as GrupoRow);
      acc.presupuestoClp += c.presupuestoClp;
      acc.solicitadoClp = (acc.solicitadoClp ?? 0) + c.solicitadoClp;
      acc.gastadoClp += c.ejecutadoClp;
      acc.enAprobacionClp += c.enAprobacionClp;
      acc.solicitudesSinOrdenClp +=
        c.solicitudesPendientesClp + c.solicitudesAprobadasSinOrdenClp;
      byArea.set(nombre, acc);
    });
    return Array.from(byArea.values())
      .map((a) => ({
        ...a,
        disponibleClp: a.presupuestoClp - a.gastadoClp,
        porcentajeEjecucion: pct(a.gastadoClp, a.presupuestoClp),
      }))
      .sort((a, b) => b.gastadoClp - a.gastadoClp);
  }, [ccRows, catalogos.data]);

  const areasChart = useMemo(() => {
    const rows = areaRows.slice(0, TOP_CHART);
    return {
      rows,
      labels: rows.map((a) => a.nombre),
      series: [
        {
          label: "Presupuesto",
          data: rows.map((a) => a.presupuestoClp),
          color: COLOR_PRESUPUESTO,
        },
        {
          label: "Solicitado",
          data: rows.map((a) => a.solicitadoClp ?? 0),
          color: COLOR_SOLICITADO,
        },
        {
          label: "Gastado",
          data: rows.map((a) => a.gastadoClp),
          color: COLOR_GASTADO,
        },
      ],
    };
  }, [areaRows]);

  const onAreaBarClick = useCallback(
    (index: number) => filtrarArea(areasChart.rows[index]?.idArea),
    [areasChart, filtrarArea],
  );

  // Naturaleza / cuenta / item groupings of /presupuesto/cuentas
  const cuentaRows = useMemo<GrupoRow[]>(() => {
    const grupos = new Map<string, GrupoRow>();
    (cuentas.data?.data ?? []).forEach((c) => {
      let keyG: string;
      let nombre: string;
      let sub: string | undefined;
      if (agruparCuentas === "cuenta") {
        keyG = String(c.codigoCuenta ?? "sin");
        nombre = c.codigoCuenta
          ? `${c.codigoCuenta} · ${clean(c.cuenta)}`
          : "Sin cuenta contable";
      } else if (agruparCuentas === "item") {
        keyG = String(c.codigoCuentaItem ?? "sin");
        nombre = c.codigoCuentaItem
          ? `${c.codigoCuentaItem} · ${clean(c.cuentaItem)}`
          : "Sin item contable";
        sub = c.codigoCuenta ? `Cuenta ${c.codigoCuenta}` : undefined;
      } else {
        keyG = String(c.codigoNaturaleza ?? "sin");
        nombre = clean(c.naturaleza) || "Sin naturaleza";
        sub = c.codigoCuenta
          ? `Cuenta ${c.codigoCuenta} · Item ${c.codigoCuentaItem ?? "-"}`
          : undefined;
      }
      const acc =
        grupos.get(keyG) ??
        ({
          key: keyG,
          nombre,
          sub,
          presupuestoClp: 0,
          solicitadoClp: null,
          gastadoClp: 0,
          enAprobacionClp: 0,
          solicitudesSinOrdenClp: 0,
          disponibleClp: 0,
          porcentajeEjecucion: 0,
        } as GrupoRow);
      acc.presupuestoClp += c.presupuestoClp;
      acc.gastadoClp += c.ejecutadoClp;
      acc.enAprobacionClp += c.enAprobacionClp;
      acc.solicitudesSinOrdenClp += c.solicitudesSinOrdenClp;
      grupos.set(keyG, acc);
    });
    return Array.from(grupos.values())
      .map((g) => ({
        ...g,
        disponibleClp: g.presupuestoClp - g.gastadoClp,
        porcentajeEjecucion: pct(g.gastadoClp, g.presupuestoClp),
      }))
      .sort(
        (a, b) =>
          Math.max(b.gastadoClp, b.presupuestoClp) -
          Math.max(a.gastadoClp, a.presupuestoClp),
      );
  }, [cuentas.data, agruparCuentas]);

  // Share of the spending not linked to any naturaleza / cuenta
  const gastoSinCuenta = useMemo(() => {
    const rows = cuentas.data?.data ?? [];
    const total = sum(rows, (r) => r.ejecutadoClp);
    const sinCuenta = sum(
      rows.filter((r) => !r.codigoCuenta || !r.codigoNaturaleza),
      (r) => r.ejecutadoClp,
    );
    return { total, sinCuenta, pct: pct(sinCuenta, total) };
  }, [cuentas.data]);

  // Monthly: budget, requested (solicitudes by month) and spent
  const mensualChart = useMemo(() => {
    const meses = mensual.data?.meses ?? [];
    const solicitadoMes = new Map(
      (solPeriodos.data ?? []).map((p) => [
        Number(String(p.periodo).split("-")[1]),
        p.montoSolicitadoClp,
      ]),
    );
    const solicitados = meses.map((m) => solicitadoMes.get(m.mes) ?? 0);
    const acumular = (v: number[]) =>
      v.reduce<number[]>((acc, x, i) => [...acc, (acc[i - 1] ?? 0) + x], []);
    const acumulado = vistaMensual === "acumulado";
    return {
      labels: meses.map((m) => MESES_CORTOS[m.mes - 1] ?? m.nombre),
      series: [
        {
          label: "Presupuesto",
          data: acumulado
            ? meses.map((m) => m.presupuestoAcumuladoClp)
            : meses.map((m) => m.presupuestoClp),
          color: COLOR_PRESUPUESTO,
        },
        {
          label: "Solicitado",
          data: acumulado ? acumular(solicitados) : solicitados,
          color: COLOR_SOLICITADO,
        },
        {
          label: "Gastado",
          data: acumulado
            ? meses.map((m) => m.ejecutadoAcumuladoClp)
            : meses.map((m) => m.ejecutadoClp),
          color: COLOR_GASTADO,
        },
      ],
    };
  }, [mensual.data, solPeriodos.data, vistaMensual]);

  // Yearly comparison (previous years + selected)
  const anualChart = useMemo(() => {
    const anios = [
      ...(aniosPrevios.data ?? []),
      ...(mensual.data ? [mensual.data] : []),
    ];
    return {
      labels: anios.map((a) => String(a.anio)),
      series: [
        {
          label: "Presupuesto",
          data: anios.map((a) => a.totales.presupuestoAnualClp),
          color: COLOR_PRESUPUESTO,
        },
        {
          label: "Gastado",
          data: anios.map((a) => a.totales.ejecutadoClp),
          color: COLOR_GASTADO,
        },
        {
          label: "Facturado",
          data: anios.map((a) => a.totales.facturadoClp),
          color: CHART_COLORS.neutral,
        },
      ],
    };
  }, [aniosPrevios.data, mensual.data]);

  const solEstadosChart = useMemo(() => estadosChart(lineas), [lineas]);
  const ordEstadosChart = useMemo(() => estadosChart(estadosOC), [estadosOC]);

  /* ------------------------- excel ------------------------- */

  const exportExcel = async () => {
    setExporting(true);
    try {
      const grupoSheet = (rows: GrupoRow[], label: string) =>
        rows.map((g) => ({
          [label]: g.nombre,
          Detalle: g.sub ?? "",
          Presupuesto: g.presupuestoClp,
          ...(g.solicitadoClp !== null ? { Solicitado: g.solicitadoClp } : {}),
          "Gastado (OC ≥6)": g.gastadoClp,
          "En aprobación": g.enAprobacionClp,
          "Solicitudes sin OC": g.solicitudesSinOrdenClp,
          Disponible: g.disponibleClp,
          "% ejecución": Number(g.porcentajeEjecucion.toFixed(2)),
        }));
      const ccName = (id: number) =>
        clean(catalogos.data?.centrosCosto.find((c) => c.id === id)?.nombre) ||
        id;
      await exportSheets("Reporte_Presupuesto", [
        {
          name: "Filtros",
          rows: [
            { Filtro: "Año", Valor: applied.anio },
            {
              Filtro: "Versión",
              Valor:
                VERSION_OPTIONS.find((v) => v.value === applied.version)
                  ?.label ?? applied.version,
            },
            {
              Filtro: "Área",
              Valor: applied.idArea
                ? (areaOptions.find((a) => a.value === applied.idArea)?.label ??
                  applied.idArea)
                : "Todas",
            },
            {
              Filtro: "Centros de costo",
              Valor: applied.centrosCosto.length
                ? applied.centrosCosto.map(ccName).join(", ")
                : "Todos",
            },
            {
              Filtro: "Gastado",
              Valor: "Órdenes de compra en estado ≥ 6",
            },
            { Filtro: "Generado", Valor: new Date().toLocaleString("es-CL") },
          ],
        },
        {
          name: "Resumen",
          rows: [
            { Indicador: "Presupuesto anual", Valor: presupuesto },
            { Indicador: "Presupuesto (suma meses)", Valor: presupuestoMeses },
            {
              Indicador: "Presupuesto a la fecha",
              Valor: t?.presupuestoALaFechaClp,
            },
            { Indicador: "Solicitado (todas las líneas)", Valor: solicitado },
            {
              Indicador: "Solicitado aprobado (≥6)",
              Valor: solicitadoAprobado,
            },
            { Indicador: "Gastado (OC ≥6)", Valor: gastado },
            { Indicador: "En aprobación (OC < 6)", Valor: enAprobacion },
            {
              Indicador: "Solicitudes sin OC",
              Valor: t?.solicitudesSinOrdenClp,
            },
            { Indicador: "Facturado", Valor: t?.facturadoClp },
            { Indicador: "Disponible", Valor: disponible },
          ],
        },
        {
          name: "Mensual",
          rows: (mensual.data?.meses ?? []).map((m, i) => ({
            Mes: m.nombre,
            Presupuesto: m.presupuestoClp,
            Solicitado: mensualChart.series[1].data[i] ?? 0,
            Gastado: m.ejecutadoClp,
            "En aprobación": m.enAprobacionClp,
            "Solicitudes sin OC": m.solicitudesSinOrdenClp,
            Facturado: m.facturadoClp,
            Desviación: m.desviacionClp,
            "Presupuesto acumulado": m.presupuestoAcumuladoClp,
            "Gastado acumulado": m.ejecutadoAcumuladoClp,
          })),
        },
        {
          name: "Centros costo",
          rows: ccRows.map((c) => ({
            Código: clean(c.codigo),
            "Centro de costo": clean(c.nombre),
            Área: clean(c.area),
            Presupuesto: c.presupuestoClp,
            Solicitado: c.solicitadoClp,
            "Gastado (OC ≥6)": c.ejecutadoClp,
            "En aprobación": c.enAprobacionClp,
            "Solicitudes pendientes": c.solicitudesPendientesClp,
            "Solicitudes aprobadas sin OC": c.solicitudesAprobadasSinOrdenClp,
            Facturado: c.facturadoClp,
            Disponible: c.disponibleClp,
            "% ejecución": c.porcentajeEjecucion,
            Estado: c.estado,
          })),
        },
        { name: "Areas", rows: grupoSheet(areaRows, "Área") },
        {
          name: "Cuentas",
          rows: (cuentas.data?.data ?? []).map((c) => ({
            Naturaleza: clean(c.naturaleza),
            "Código naturaleza": c.codigoNaturaleza,
            "Cuenta contable": c.codigoCuenta ?? "",
            Cuenta: clean(c.cuenta),
            "Item contable": c.codigoCuentaItem ?? "",
            Item: clean(c.cuentaItem),
            "Centros de costo": c.cantidadCentrosCosto,
            Presupuesto: c.presupuestoClp,
            "Gastado (OC ≥6)": c.ejecutadoClp,
            "En aprobación": c.enAprobacionClp,
            "Solicitudes sin OC": c.solicitudesSinOrdenClp,
            Facturado: c.facturadoClp,
            Disponible: c.disponibleClp,
            "% ejecución": c.porcentajeEjecucion,
            Estado: c.estado,
          })),
        },
        {
          name: "Estados solicitudes",
          rows: lineas.map((e) => ({
            Estado: e.nombre,
            "≥6": e.idEstado >= ESTADO_GASTADO_MIN ? "Sí" : "No",
            Líneas: e.cantidad,
            Monto: e.montoClp,
          })),
        },
        {
          name: "Estados OC",
          rows: estadosOC.map((e) => ({
            Estado: e.nombre,
            "Gastado (≥6)": e.idEstado >= ESTADO_GASTADO_MIN ? "Sí" : "No",
            "N° OC": e.cantidad,
            Monto: e.montoClp,
          })),
        },
      ]);
    } catch (e) {
      console.error("Error exportando reporte presupuesto:", e);
      showMsg(toast, "error", "Error", "No fue posible generar el Excel");
    } finally {
      setExporting(false);
    }
  };

  /* ------------------------- table templates ------------------------- */

  const grupoTable = (
    rows: GrupoRow[],
    header: string,
    opts: { solicitado: boolean; onClick?: (g: GrupoRow) => void },
  ) => (
    <DataTable
      value={rows}
      size="small"
      scrollable
      scrollHeight="420px"
      sortMode="single"
      removableSort
      stripedRows
      emptyMessage="Sin datos para los filtros seleccionados"
      className="fd-table"
    >
      <Column
        header={header}
        field="nombre"
        sortable
        style={{ minWidth: "12rem" }}
        body={(g: GrupoRow) =>
          opts.onClick && g.idArea ? (
            <FilterLink
              name={g.nombre}
              sub={g.sub}
              title="Filtrar el dashboard por esta área"
              onClick={() => opts.onClick?.(g)}
            />
          ) : (
            <div>
              <div className="fd-prov-name">{g.nombre}</div>
              {g.sub ? <div className="fd-prov-rut">{g.sub}</div> : null}
            </div>
          )
        }
      />
      <Column
        header="Presupuesto"
        field="presupuestoClp"
        sortable
        body={(g: GrupoRow) => <Money value={g.presupuestoClp} />}
      />
      {opts.solicitado ? (
        <Column
          header="Solicitado"
          field="solicitadoClp"
          sortable
          body={(g: GrupoRow) => <Money value={g.solicitadoClp} />}
        />
      ) : null}
      <Column
        header="Gastado"
        field="gastadoClp"
        sortable
        body={(g: GrupoRow) => <Money value={g.gastadoClp} />}
      />
      <Column
        header="En aprob."
        field="enAprobacionClp"
        sortable
        body={(g: GrupoRow) => <Money value={g.enAprobacionClp} />}
      />
      <Column
        header="Disponible"
        field="disponibleClp"
        sortable
        body={(g: GrupoRow) => (
          <span className={`fd-num${g.disponibleClp < 0 ? " fd-neg" : ""}`}>
            {formatMoney(g.disponibleClp)}
          </span>
        )}
      />
      <Column
        header="% Ejec."
        field="porcentajeEjecucion"
        sortable
        style={{ minWidth: "8rem" }}
        body={(g: GrupoRow) =>
          ejecucionBody(g.porcentajeEjecucion, g.presupuestoClp)
        }
      />
    </DataTable>
  );

  /* ------------------------- render ------------------------- */

  const areaNombre = applied.idArea
    ? areaOptions.find((a) => a.value === applied.idArea)?.label
    : null;
  const ccNombre =
    applied.centrosCosto.length === 1
      ? clean(
          catalogos.data?.centrosCosto.find(
            (c) => c.id === applied.centrosCosto[0],
          )?.nombre,
        )
      : null;
  const presupuestoInconsistente =
    !mensual.loading && Math.abs(presupuestoMeses - presupuesto) >= 1;

  return (
    <div className="fd-dashboard rp-dashboard">
      <Toast ref={toast} />

      {/* ------------------------- HEADER ------------------------- */}
      <header className="fd-hero">
        <div className="fd-hero-main">
          <div className="fd-hero-icon" aria-hidden="true">
            <i className="pi pi-wallet" />
          </div>
          <div className="fd-hero-text">
            <span className="fd-eyebrow">Compras · Presupuesto</span>
            <h4 className="fd-title">Reporte de presupuesto</h4>
            <div className="fd-meta">
              <span className="fd-meta-item">
                <i className="pi pi-calendar" />
                Año {applied.anio}
              </span>
              <span className="fd-meta-item">
                <i className="pi pi-bookmark" />
                Versión{" "}
                {
                  VERSION_OPTIONS.find((v) => v.value === applied.version)
                    ?.label
                }
              </span>
              <span className="fd-meta-item">
                <i className="pi pi-info-circle" />
                Gastado = OC en estado ≥ 6
              </span>
              {activeCount > 0 ? (
                <span className="fd-meta-item fd-meta-accent">
                  <i className="pi pi-filter-fill" />
                  {activeCount}{" "}
                  {activeCount === 1 ? "filtro activo" : "filtros activos"}
                </span>
              ) : null}
            </div>
          </div>
        </div>
        <div className="fd-hero-actions">
          <Button
            type="button"
            icon="pi pi-refresh"
            label="Actualizar"
            className="fd-btn fd-btn-ghost"
            loading={anyLoading}
            onClick={reloadAll}
          />
          <Button
            type="button"
            icon="pi pi-file-excel"
            label="Exportar Excel"
            className="fd-btn fd-btn-excel"
            loading={exporting}
            disabled={anyLoading}
            onClick={exportExcel}
          />
        </div>
      </header>

      {/* ------------------------- FILTROS ------------------------- */}
      <section className="fd-filters" aria-label="Filtros del reporte">
        <div className="fd-filters-head">
          <span className="fd-filters-title">
            <i className="pi pi-sliders-h" />
            Filtros
          </span>
          {isDirty ? (
            <span className="fd-dirty">
              <span className="fd-dirty-dot" />
              Cambios sin aplicar
            </span>
          ) : null}
        </div>
        <div className="fd-filter-grid">
          <div className="fd-field">
            <label htmlFor="pp-anio">Año</label>
            <Dropdown
              inputId="pp-anio"
              value={form.anio}
              options={anioOptions}
              onChange={(e) => setForm((f) => ({ ...f, anio: e.value }))}
            />
          </div>
          <div className="fd-field">
            <label htmlFor="pp-version">Versión presupuesto</label>
            <Dropdown
              inputId="pp-version"
              value={form.version}
              options={VERSION_OPTIONS}
              onChange={(e) => setForm((f) => ({ ...f, version: e.value }))}
            />
          </div>
          <div className="fd-field">
            <label htmlFor="pp-area">Área</label>
            <Dropdown
              inputId="pp-area"
              value={form.idArea}
              options={areaOptions}
              placeholder="Todas"
              showClear
              filter
              onChange={(e) => onAreaChange(e.value ?? null)}
            />
          </div>
          <div className="fd-field">
            <label htmlFor="pp-cc">Centros de costo</label>
            <MultiSelect
              inputId="pp-cc"
              value={form.centrosCosto}
              options={centroCostoOptions}
              placeholder="Todos"
              filter
              showClear
              maxSelectedLabels={1}
              selectedItemsLabel="{0} centros de costo"
              onChange={(e) =>
                setForm((f) => ({ ...f, centrosCosto: e.value ?? [] }))
              }
            />
          </div>
        </div>
        <div className="fd-filters-foot">
          <div className="fd-quick">
            <span className="fd-quick-label">
              Presupuesto por año calendario · solicitudes y OC por fecha de
              creación dentro del año
            </span>
          </div>
          <div className="fd-filter-actions">
            {areaNombre ? (
              <span className="fd-chip">
                <i className="pi pi-map" />
                <span className="fd-chip-text">{areaNombre}</span>
                <button
                  type="button"
                  className="fd-chip-close"
                  title="Quitar filtro de área"
                  aria-label="Quitar filtro de área"
                  onClick={() => patchBoth({ idArea: null })}
                >
                  <i className="pi pi-times" />
                </button>
              </span>
            ) : null}
            {ccNombre ? (
              <span className="fd-chip">
                <i className="pi pi-sitemap" />
                <span className="fd-chip-text">{ccNombre}</span>
                <button
                  type="button"
                  className="fd-chip-close"
                  title="Quitar filtro de centro de costo"
                  aria-label="Quitar filtro de centro de costo"
                  onClick={() => patchBoth({ centrosCosto: [] })}
                >
                  <i className="pi pi-times" />
                </button>
              </span>
            ) : null}
            <Button
              type="button"
              label="Limpiar"
              icon="pi pi-filter-slash"
              className="fd-btn fd-btn-subtle"
              onClick={() => {
                setForm(defaultFiltros());
                setApplied(defaultFiltros());
              }}
            />
            <Button
              type="button"
              label="Aplicar filtros"
              icon="pi pi-check"
              className={`fd-btn fd-btn-primary${isDirty ? " is-dirty" : ""}`}
              onClick={() => setApplied({ ...form })}
            />
          </div>
        </div>
      </section>

      {/* ------------------------- KPIs ------------------------- */}
      <div className="fd-kpis">
        <KpiCard
          title="Presupuesto anual"
          icon="pi-wallet"
          color={COLOR_PRESUPUESTO}
          loading={mensual.loading}
          value={mensual.error ? "Sin datos" : formatMoney(presupuesto)}
          hint={
            presupuestoInconsistente
              ? `Suma de los meses: ${formatMoney(presupuestoMeses)}`
              : `A la fecha: ${formatMoney(t?.presupuestoALaFechaClp)}`
          }
          tone={presupuestoInconsistente ? "warning" : "default"}
        />
        <KpiCard
          title="Solicitado"
          icon="pi-file-edit"
          color={COLOR_SOLICITADO}
          loading={solEstados.loading}
          value={formatMoney(solicitado)}
          progress={pct(solicitadoAprobado, solicitado)}
          hint={`Aprobado (≥6): ${formatMoney(solicitadoAprobado)}`}
        />
        <KpiCard
          title="Gastado (OC estado ≥ 6)"
          icon="pi-shopping-cart"
          color={COLOR_GASTADO}
          tone={presupuesto && gastado > presupuesto ? "danger" : "default"}
          loading={ordEstados.loading}
          value={formatMoney(gastado)}
          progress={pct(gastado, presupuesto)}
          hint={
            presupuesto
              ? `${formatPercent(pct(gastado, presupuesto))} del presupuesto`
              : "Sin presupuesto para comparar"
          }
        />
        <KpiCard
          title="Disponible"
          icon="pi-chart-line"
          color={disponible < 0 ? CHART_COLORS.diferencia : COLOR_PRESUPUESTO}
          tone={disponible < 0 ? "danger" : "success"}
          loading={mensual.loading || ordEstados.loading}
          value={formatMoney(disponible)}
          hint="Presupuesto − gastado"
        />
        <KpiCard
          title="% ejecución a la fecha"
          icon="pi-percentage"
          color={COLOR_GASTADO}
          loading={mensual.loading || ordEstados.loading}
          value={
            t?.presupuestoALaFechaClp
              ? formatPercent(pct(gastado, t.presupuestoALaFechaClp))
              : "-"
          }
          progress={pct(gastado, t?.presupuestoALaFechaClp ?? 0)}
          hint="Gastado vs presupuesto hasta el mes actual"
        />
        <KpiCard
          title="En aprobación"
          icon="pi-hourglass"
          color={CHART_COLORS.pendiente}
          tone="warning"
          loading={ordEstados.loading}
          value={formatMoney(enAprobacion)}
          hint="OC aún no aprobadas (estado < 6)"
        />
        <KpiCard
          title="Solicitudes sin OC"
          icon="pi-inbox"
          color={CHART_COLORS.neutral}
          loading={mensual.loading}
          value={formatMoney(t?.solicitudesSinOrdenClp)}
          hint="Comprometido, aún sin orden de compra"
        />
        <KpiCard
          title="Facturado"
          icon="pi-receipt"
          color={CHART_COLORS.contabilizado}
          loading={mensual.loading}
          value={formatMoney(t?.facturadoClp)}
          progress={pct(t?.facturadoClp ?? 0, gastado)}
          hint={`${formatPercent(pct(t?.facturadoClp ?? 0, gastado))} de lo gastado`}
        />
      </div>

      {/* ------------------------- MENSUAL + ANUAL ------------------------- */}
      <div className="fd-grid fd-grid-2-1">
        <ChartCard
          title="Presupuesto vs solicitado vs gastado por mes"
          subtitle={`Año ${applied.anio}`}
          loading={mensual.loading || solPeriodos.loading}
          error={mensual.error ?? solPeriodos.error}
          onRetry={() => {
            mensual.reload();
            solPeriodos.reload();
          }}
          empty={!mensual.data?.meses?.length}
          actions={
            <SelectButton
              value={vistaMensual}
              options={VISTA_MENSUAL_OPTIONS}
              onChange={(e) => e.value && setVistaMensual(e.value)}
              className="fd-select-sm"
              allowEmpty={false}
            />
          }
        >
          <MultiBarChart
            labels={mensualChart.labels}
            series={mensualChart.series}
          />
        </ChartCard>

        <ChartCard
          title="Comparación anual"
          subtitle={`Últimos ${ANIOS_COMPARACION} años`}
          loading={aniosPrevios.loading || mensual.loading}
          error={aniosPrevios.error ?? mensual.error}
          onRetry={() => {
            aniosPrevios.reload();
            mensual.reload();
          }}
          empty={!anualChart.labels.length}
        >
          <MultiBarChart
            labels={anualChart.labels}
            series={anualChart.series}
          />
        </ChartCard>
      </div>

      <ChartCard
        title="Detalle mensual"
        subtitle="Desviación = presupuesto − gastado"
        loading={mensual.loading}
        error={mensual.error}
        onRetry={mensual.reload}
        empty={!mensual.data?.meses?.length}
        height={200}
      >
        <DataTable
          value={mensual.data?.meses ?? []}
          size="small"
          stripedRows
          className="fd-table"
        >
          <Column header="Mes" field="nombre" />
          <Column
            header="Presupuesto"
            body={(m: MesRow) => <Money value={m.presupuestoClp} />}
          />
          <Column
            header="Solicitado"
            body={(_m: MesRow, o: { rowIndex: number }) => (
              <Money value={mensualChart.series[1].data[o.rowIndex]} />
            )}
          />
          <Column
            header="Gastado"
            body={(m: MesRow) => <Money value={m.ejecutadoClp} />}
          />
          <Column
            header="En aprob."
            body={(m: MesRow) => <Money value={m.enAprobacionClp} />}
          />
          <Column
            header="Sol. sin OC"
            body={(m: MesRow) => <Money value={m.solicitudesSinOrdenClp} />}
          />
          <Column
            header="Facturado"
            body={(m: MesRow) => <Money value={m.facturadoClp} />}
          />
          <Column
            header="Desviación"
            body={(m: MesRow) => (
              <span className={`fd-num${m.desviacionClp < 0 ? " fd-neg" : ""}`}>
                {formatMoney(m.desviacionClp)}
              </span>
            )}
          />
          <Column
            header="% Ejec. acum."
            style={{ minWidth: "8rem" }}
            body={(m: MesRow) =>
              ejecucionBody(
                m.porcentajeEjecucionAcumulado,
                m.presupuestoAcumuladoClp,
              )
            }
          />
        </DataTable>
      </ChartCard>

      {/* ------------------------- ESTADOS ------------------------- */}
      <div className="rp-section">
        <h5 className="rp-section-title">
          <i className="pi pi-check-square" />
          Montos por estado de aprobación
        </h5>
        <span className="rp-section-desc">
          Estados ≥ 6 cuentan como aprobado (solicitudes) y gastado (OC)
        </span>
      </div>
      <div className="fd-grid fd-grid-1-1">
        <ChartCard
          title="Solicitado por estado"
          subtitle={`Líneas de solicitud · aprobado (≥6): ${formatMoneyCompact(solicitadoAprobado)}`}
          loading={solEstados.loading}
          error={solEstados.error}
          onRetry={solEstados.reload}
          empty={!solEstadosChart.labels.length}
        >
          <MultiBarChart
            labels={solEstadosChart.labels}
            series={solEstadosChart.series}
            horizontal
            showLegend={false}
            maxLabelLength={28}
          />
        </ChartCard>
        <ChartCard
          title="Órdenes de compra por estado"
          subtitle={`Gastado (≥6): ${formatMoneyCompact(gastado)}`}
          loading={ordEstados.loading}
          error={ordEstados.error}
          onRetry={ordEstados.reload}
          empty={!ordEstadosChart.labels.length}
        >
          <MultiBarChart
            labels={ordEstadosChart.labels}
            series={ordEstadosChart.series}
            horizontal
            showLegend={false}
            maxLabelLength={28}
          />
        </ChartCard>
      </div>

      {/* ------------------------- CENTROS DE COSTO ------------------------- */}
      <div className="rp-section">
        <h5 className="rp-section-title">
          <i className="pi pi-sitemap" />
          Centros de costo y áreas
        </h5>
        <span className="rp-section-desc">
          Clic en una barra o nombre para filtrar el dashboard
        </span>
      </div>
      <div className="fd-grid fd-grid-1-1">
        <ChartCard
          title={`Top ${TOP_CHART} centros de costo`}
          subtitle="Por mayor presupuesto o gasto"
          loading={centros.loading || solCentros.loading}
          error={centros.error ?? solCentros.error}
          onRetry={() => {
            centros.reload();
            solCentros.reload();
          }}
          empty={!topCentros.length}
          height={420}
        >
          <MultiBarChart
            labels={centrosChart.labels}
            series={centrosChart.series}
            horizontal
            height={420}
            onBarClick={onCentroBarClick}
          />
        </ChartCard>
        <ChartCard
          title="Detalle por centro de costo"
          subtitle={`${formatNumber(ccRows.length)} centros de costo`}
          loading={centros.loading || solCentros.loading}
          error={centros.error ?? solCentros.error}
          onRetry={() => {
            centros.reload();
            solCentros.reload();
          }}
          height={420}
        >
          <DataTable
            value={ccRows}
            size="small"
            scrollable
            scrollHeight="420px"
            sortMode="single"
            sortField="ejecutadoClp"
            sortOrder={-1}
            removableSort
            stripedRows
            emptyMessage="Sin centros de costo para los filtros seleccionados"
            className="fd-table"
          >
            <Column
              header="Centro de costo"
              field="nombre"
              sortable
              style={{ minWidth: "12rem" }}
              body={(c: CentroCostoPpto) => (
                <FilterLink
                  name={c.nombre}
                  sub={c.area}
                  title="Filtrar dashboard por este centro de costo"
                  onClick={() => filtrarCentroCosto(c.idCentroCosto)}
                />
              )}
            />
            <Column
              header="Presupuesto"
              field="presupuestoClp"
              sortable
              body={(c: CentroCostoPpto) => <Money value={c.presupuestoClp} />}
            />
            <Column
              header="Solicitado"
              field="solicitadoClp"
              sortable
              body={(c: { solicitadoClp: number }) => (
                <Money value={c.solicitadoClp} />
              )}
            />
            <Column
              header="Gastado"
              field="ejecutadoClp"
              sortable
              body={(c: CentroCostoPpto) => <Money value={c.ejecutadoClp} />}
            />
            <Column
              header="Disponible"
              field="disponibleClp"
              sortable
              body={(c: CentroCostoPpto) => (
                <span
                  className={`fd-num${c.disponibleClp < 0 ? " fd-neg" : ""}`}
                >
                  {formatMoney(c.disponibleClp)}
                </span>
              )}
            />
            <Column
              header="Estado"
              field="estado"
              sortable
              body={(c: CentroCostoPpto) => estadoPptoTag(c.estado)}
            />
          </DataTable>
        </ChartCard>
      </div>

      <div className="fd-grid fd-grid-1-1">
        <ChartCard
          title="Presupuesto vs solicitado vs gastado por área"
          subtitle={`Top ${TOP_CHART} áreas`}
          loading={centros.loading || solCentros.loading}
          error={centros.error ?? solCentros.error}
          onRetry={centros.reload}
          empty={!areasChart.labels.length}
          height={380}
        >
          <MultiBarChart
            labels={areasChart.labels}
            series={areasChart.series}
            horizontal
            height={380}
            onBarClick={onAreaBarClick}
          />
        </ChartCard>
        <ChartCard
          title="Detalle por área"
          subtitle="Suma de sus centros de costo"
          loading={centros.loading || solCentros.loading}
          error={centros.error ?? solCentros.error}
          onRetry={centros.reload}
          height={380}
        >
          {grupoTable(areaRows, "Área", {
            solicitado: true,
            onClick: (g) => filtrarArea(g.idArea),
          })}
        </ChartCard>
      </div>

      {/* ------------------------- CUENTAS ------------------------- */}
      <div className="rp-section">
        <h5 className="rp-section-title">
          <i className="pi pi-book" />
          Naturaleza, cuenta e item contable
        </h5>
        <span className="rp-section-desc">
          El gasto se asigna según la naturaleza asociada al ítem de cada línea
        </span>
      </div>
      <ChartCard
        title="Presupuesto y gasto por clasificación contable"
        subtitle={
          gastoSinCuenta.total
            ? `${formatPercent(gastoSinCuenta.pct)} del gasto (${formatMoneyCompact(
                gastoSinCuenta.sinCuenta,
              )}) está en ítems sin naturaleza / cuenta asignada`
            : "Presupuesto cargado por naturaleza, cuenta e item"
        }
        loading={cuentas.loading}
        error={cuentas.error}
        onRetry={cuentas.reload}
        empty={!cuentaRows.length}
        height={240}
        actions={
          <SelectButton
            value={agruparCuentas}
            options={AGRUPAR_CUENTAS_OPTIONS}
            onChange={(e) => e.value && setAgruparCuentas(e.value)}
            className="fd-select-sm"
            allowEmpty={false}
          />
        }
      >
        {grupoTable(
          cuentaRows,
          AGRUPAR_CUENTAS_OPTIONS.find((o) => o.value === agruparCuentas)
            ?.label ?? "",
          { solicitado: false },
        )}
      </ChartCard>

      <ChartCard
        title="Asociación de ítems a naturalezas"
        subtitle="Revisión de qué ítems de compra quedan sin naturaleza contable (carga pesada, bajo demanda)"
        height={120}
      >
        {mostrarItems ? (
          <AsociacionItems filtros={applied} />
        ) : (
          <Button
            type="button"
            icon="pi pi-download"
            label="Cargar asociación de ítems"
            size="small"
            outlined
            onClick={() => setMostrarItems(true)}
          />
        )}
      </ChartCard>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Item association (on demand: the endpoint lists every item)          */
/* ------------------------------------------------------------------ */

function AsociacionItems({ filtros }: { filtros: Filtros }) {
  const data = useReporte<NaturalezasItems>(
    () =>
      api.GET_REPORTES.getPresupuestoNaturalezasItems(
        filtrosPresupuesto(filtros),
      ),
    JSON.stringify(filtros),
  );

  if (data.loading) {
    return <div className="fd-note">Cargando asociación de ítems…</div>;
  }
  if (data.error || !data.data) {
    return (
      <ChartCard
        title=""
        error={data.error ?? "Sin datos"}
        onRetry={data.reload}
        height={100}
      />
    );
  }

  const tot = data.data.totales;
  const porRevisar = data.data.itemsPorRevisar ?? [];
  const columnas = porRevisar.length
    ? Object.keys(porRevisar[0]).filter(
        (k) => typeof porRevisar[0][k] !== "object",
      )
    : [];

  return (
    <div className="pp-items">
      <div className="fd-note">
        {formatNumber(tot.itemsAsociados)} ítems asociados en{" "}
        {formatNumber(tot.naturalezasConItems)}{" "}
        {tot.naturalezasConItems === 1 ? "naturaleza" : "naturalezas"} ·{" "}
        {formatNumber(tot.itemsSinAsociar)} sin asociar (
        {formatMoney(tot.montoItemsSinAsociarClp)}) ·{" "}
        {formatNumber(tot.itemsMultiples)} con más de una naturaleza
      </div>
      <DataTable
        value={data.data.naturalezas}
        size="small"
        stripedRows
        className="fd-table"
      >
        <Column
          header="Naturaleza"
          body={(n: CuentaRow) => clean(n.naturaleza) || "Sin naturaleza"}
        />
        <Column
          header="Cuenta / item"
          body={(n: CuentaRow) =>
            n.codigoCuenta
              ? `${n.codigoCuenta} · ${n.codigoCuentaItem ?? "-"}`
              : "-"
          }
        />
        <Column
          header="Ítems"
          body={(n: CuentaRow & { itemsAsociados: unknown[] }) => (
            <Count value={n.itemsAsociados?.length ?? 0} />
          )}
        />
        <Column
          header="Presupuesto"
          body={(n: CuentaRow) => <Money value={n.presupuestoClp} />}
        />
        <Column
          header="Gastado"
          body={(n: CuentaRow) => <Money value={n.ejecutadoClp} />}
        />
      </DataTable>
      <h6 className="pp-items-title">
        Ítems por revisar ({formatNumber(porRevisar.length)})
      </h6>
      {porRevisar.length ? (
        <DataTable
          value={porRevisar}
          size="small"
          paginator
          rows={10}
          stripedRows
          className="fd-table"
        >
          {columnas.map((k) => (
            <Column key={k} field={k} header={k} />
          ))}
        </DataTable>
      ) : (
        <div className="fd-note">No hay ítems por revisar.</div>
      )}
    </div>
  );
}
